import React, { useState, useMemo } from 'react'
import { Popup, Search, Cell, Toast } from 'react-vant'
import { useTranslation } from 'react-i18next'
import type { StateOption } from '@/utils/countryData'

interface StateSelectorProps {
    value: string
    onChange: (value: string, stateId?: number) => void
    placeholder?: string
    options: StateOption[]
    disabled?: boolean
}

const StateSelector: React.FC<StateSelectorProps> = ({
    value,
    onChange,
    placeholder,
    options,
    disabled = false
}) => {
    const { t } = useTranslation()
    const [visible, setVisible] = useState(false)
    const [searchText, setSearchText] = useState('')

    // 获取选中的州/省
    const selectedState = useMemo(() => {
        return options.find(option => option.value === value)
    }, [options, value])

    // 过滤州/省列表
    const filteredStates = useMemo(() => {
        if (!searchText.trim()) {
            return options
        }
        const searchLower = searchText.toLowerCase()
        return options.filter(state =>
            state.label.toLowerCase().includes(searchLower) ||
            state.value.toLowerCase().includes(searchLower)
        )
    }, [options, searchText])

    // 处理选择
    const handleSelect = (state: StateOption) => {
        onChange(state.value, state.id)
        setVisible(false)
        setSearchText('')
    }

    // 处理点击
    const handleClick = () => {
        if (disabled) {
            Toast.info(t('请选择国家或地区'))
            return
        }
        if (options.length === 0) {
            Toast.info(t('该国家或地区暂无州或省或区数据'))
            return
        }
        setVisible(true)
    }

    return (
        <>
            <div
                onClick={handleClick}
                className={`w-full px-4 h-11.5 border-[0.5px] border-[#808080] border-solid rounded-2.5 bg-white flex items-center justify-between ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
                <span className={`text-3 ${selectedState ? 'text-black' : 'text-[#1A1A1A]/30'}`}>
                    {selectedState ? selectedState.label : placeholder}
                </span>
                <img src="/icon/select-down.svg" alt="" />
            </div>

            <Popup
                visible={visible}
                onClose={() => {
                    setVisible(false)
                    setSearchText('')
                }}
                position="bottom"
                round
                style={{ height: '70%' }}
            >
                <div className="flex flex-col h-full">
                    {/* 标题 */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                        <span
                            className="text-sm text-gray-500 cursor-pointer"
                            onClick={() => {
                                setVisible(false)
                                setSearchText('')
                            }}
                        >
                            {t('取消')}
                        </span>
                        <span className="text-base font-medium">{t('选择州或省或区')}</span>
                        <span className="w-12"></span>
                    </div>

                    {/* 搜索框 */}
                    <Search
                        value={searchText}
                        onChange={setSearchText}
                        placeholder={t('搜索州或省或区')}
                        shape="round"
                    />

                    {/* 州/省列表 */}
                    <div className="flex-1 overflow-y-auto">
                        {filteredStates.length > 0 ? (
                            filteredStates.map((state) => (
                                <Cell
                                    key={state.id}
                                    title={state.label}
                                    clickable
                                    onClick={() => handleSelect(state)}
                                    className={value === state.value ? 'bg-blue-50' : ''}
                                    rightIcon={value === state.value ? <span className="text-blue-600">✓</span> : null}
                                />
                            ))
                        ) : (
                            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                                <div className="text-sm">{t('未找到匹配的州或省或区')}</div>
                            </div>
                        )}
                    </div>
                </div>
            </Popup>
        </>
    )
}

export default StateSelector
